import React, { useState } from 'react';
import { Button, Card, Field, IconButton, Input, Note, Progress, Sym } from '../../../components/app2';
import authService from '../../../utils/authService';
import AlertService from '../../../utils/alertService';

/**
 * Cambio de contraseña desde Mi perfil (rediseño jul-2026).
 *
 * CORRECCIÓN DE FONDO: este formulario NO cambiaba la contraseña. El manejador
 * tenía un `// TODO`, esperaba dos segundos con un setTimeout y luego mostraba
 * "La contraseña ha sido actualizada exitosamente" — con la contraseña intacta.
 * Ahora usa el MISMO servicio que la pantalla de cambio obligatorio
 * (authService.changePassword → POST /api/users/me/change-password), que ya
 * existía y funciona, y reporta el resultado real (éxito o el error del
 * servidor) con el diálogo del sistema en lugar de un alert() del navegador.
 *
 * Se conservan el medidor de fortaleza, la lista de requisitos y las
 * validaciones en vivo.
 */

const STRENGTH_LEVELS = [
  { min: 0, max: 1, label: 'Muy débil', color: 'var(--danger)' },
  { min: 2, max: 2, label: 'Débil', color: 'var(--warn)' },
  { min: 3, max: 3, label: 'Regular', color: 'var(--warn)' },
  { min: 4, max: 4, label: 'Fuerte', color: 'var(--brand)' },
  { min: 5, max: 5, label: 'Muy fuerte', color: 'var(--ok)' },
];

const REQUISITOS = [
  { test: (v) => (v || '').length >= 8, label: 'Al menos 8 caracteres' },
  { test: (v) => /[a-z]/.test(v || ''), label: 'Una letra minúscula' },
  { test: (v) => /[A-Z]/.test(v || ''), label: 'Una letra mayúscula' },
  { test: (v) => /\d/.test(v || ''), label: 'Un número' },
];

const PasswordChangeSection = ({ onDataChange }) => {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Password strength indicator
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: 'var(--line-2)', percentage: 0 };

    let strength = 0;
    const checks = [
      /.{8,}/,        // At least 8 characters
      /[a-z]/,        // Lowercase
      /[A-Z]/,        // Uppercase
      /\d/,           // Numbers
      /[^a-zA-Z\d]/,  // Special characters
    ];

    checks.forEach((regex) => {
      if (regex.test(password)) strength += 1;
    });

    const level = STRENGTH_LEVELS.find(l => strength >= l.min && strength <= l.max);

    return {
      strength,
      label: level?.label || '',
      color: level?.color || 'var(--line-2)',
      percentage: (strength / 5) * 100,
    };
  };

  // Validate individual fields
  const validateField = (field, value) => {
    let error = null;

    switch (field) {
      case 'currentPassword':
        if (!value) {
          error = 'La contraseña actual es requerida';
        }
        break;

      case 'newPassword':
        if (!value) {
          error = 'La nueva contraseña es requerida';
        } else if (value?.length < 8) {
          error = 'La contraseña debe tener al menos 8 caracteres';
        } else if (value === passwordData?.currentPassword) {
          error = 'La nueva contraseña debe ser diferente a la actual';
        }

        // Also validate confirm password when new password changes
        if (passwordData?.confirmPassword && value !== passwordData?.confirmPassword) {
          setErrors(prev => ({ ...prev, confirmPassword: 'Las contraseñas no coinciden' }));
        } else if (passwordData?.confirmPassword && value === passwordData?.confirmPassword) {
          setErrors(prev => ({ ...prev, confirmPassword: null }));
        }
        break;

      case 'confirmPassword':
        if (!value) {
          error = 'Confirma tu nueva contraseña';
        } else if (value !== passwordData?.newPassword) {
          error = 'Las contraseñas no coinciden';
        }
        break;

      default:
        break;
    }

    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }

    return !error;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));

    // Clear errors for this field
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }

    // Validate in real-time
    validateField(field, value);

    // Notify parent of changes
    if (value) {
      onDataChange?.();
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev?.[field] }));
  };

  // Handle password change submission
  const handlePasswordChange = async (e) => {
    e?.preventDefault?.();

    // Validate all fields
    const isValid = ['currentPassword', 'newPassword', 'confirmPassword']?.every(field =>
      validateField(field, passwordData?.[field])
    );

    if (!isValid) return;

    try {
      setLoading(true);

      const result = await authService?.changePassword(
        passwordData?.currentPassword,
        passwordData?.newPassword,
      );

      if (result?.success) {
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setErrors({});
        await AlertService?.success(
          'Contraseña actualizada',
          'Usa la nueva contraseña la próxima vez que inicies sesión.',
          { autoClose: true },
        );
      } else {
        setErrors(prev => ({ ...prev, general: result?.error || 'No se pudo cambiar la contraseña.' }));
        await AlertService?.error(
          'No se pudo cambiar la contraseña',
          result?.error || 'Revisa la contraseña actual e inténtalo de nuevo.',
        );
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setErrors(prev => ({ ...prev, general: 'Error de conexión. Inténtalo nuevamente.' }));
      await AlertService?.error(
        'Error de conexión',
        'No se pudo contactar al servidor. Verifica tu conexión e inténtalo nuevamente.',
      );
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(passwordData?.newPassword);

  const ojo = (campo, visible) => (
    <IconButton
      icon={visible ? 'visibility_off' : 'visibility'}
      title={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      type="button"
      onClick={() => togglePasswordVisibility(campo)}
      style={{
        position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
        width: 34, height: 34, border: 'none', background: 'none',
      }}
    />
  );

  const puedeEnviar = passwordData?.currentPassword &&
    passwordData?.newPassword &&
    passwordData?.confirmPassword &&
    !['currentPassword', 'newPassword', 'confirmPassword'].some(k => errors?.[k]);

  return (
    <Card>
      <h3 className="a2-card-title" style={{ fontSize: '1.1rem' }}>Cambiar contraseña</h3>
      <p className="a2-card-sub" style={{ marginBottom: 20 }}>
        Necesitas tu contraseña actual para confirmar el cambio
      </p>

      <form onSubmit={handlePasswordChange}>
        <Field
          label="Contraseña actual *"
          htmlFor="current-password"
          error={errors?.currentPassword}
          style={{ marginBottom: 16 }}
        >
          <div style={{ position: 'relative' }}>
            <Input
              id="current-password"
              type={showPasswords?.current ? 'text' : 'password'}
              value={passwordData?.currentPassword}
              onChange={(e) => handleInputChange('currentPassword', e?.target?.value)}
              placeholder="Ingresa tu contraseña actual"
              autoComplete="current-password"
              style={{ paddingRight: 46 }}
            />
            {ojo('current', showPasswords?.current)}
          </div>
        </Field>

        <Field
          label="Nueva contraseña *"
          htmlFor="new-password"
          error={errors?.newPassword}
          style={{ marginBottom: 16 }}
        >
          <div style={{ position: 'relative' }}>
            <Input
              id="new-password"
              type={showPasswords?.new ? 'text' : 'password'}
              value={passwordData?.newPassword}
              onChange={(e) => handleInputChange('newPassword', e?.target?.value)}
              placeholder="Ingresa tu nueva contraseña"
              autoComplete="new-password"
              style={{ paddingRight: 46 }}
            />
            {ojo('new', showPasswords?.new)}
          </div>

          {passwordData?.newPassword ? (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 8, marginBottom: 6,
                }}
              >
                <span className="a2-tcell-muted">Fortaleza de la contraseña</span>
                <span style={{ font: "700 .78rem 'Hanken Grotesk'", color: passwordStrength.color }}>
                  {passwordStrength.label}
                </span>
              </div>
              <Progress
                value={passwordStrength.percentage}
                color={passwordStrength.color}
                label={`Fortaleza: ${passwordStrength.label}`}
              />
            </div>
          ) : null}
        </Field>

        <Field
          label="Confirmar nueva contraseña *"
          htmlFor="confirm-password"
          error={errors?.confirmPassword}
          style={{ marginBottom: 16 }}
        >
          <div style={{ position: 'relative' }}>
            <Input
              id="confirm-password"
              type={showPasswords?.confirm ? 'text' : 'password'}
              value={passwordData?.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
              placeholder="Confirma tu nueva contraseña"
              autoComplete="new-password"
              style={{ paddingRight: 46 }}
            />
            {ojo('confirm', showPasswords?.confirm)}
          </div>
        </Field>

        {/* Requisitos: se van marcando conforme se cumplen */}
        <div
          className="a2-surface-2"
          style={{ borderRadius: 12, border: '1px solid var(--line-2)', padding: '14px 16px' }}
        >
          <div className="a2-tcell-muted" style={{ marginBottom: 8 }}>Requisitos de la contraseña</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 8 }}>
            {REQUISITOS.map((r) => {
              const ok = r.test(passwordData?.newPassword);
              return (
                <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Sym
                    name={ok ? 'check_circle' : 'radio_button_unchecked'}
                    size={17}
                    style={{ color: ok ? 'var(--ok)' : 'var(--faint)', flexShrink: 0 }}
                  />
                  <span
                    style={{
                      font: "500 .84rem 'Hanken Grotesk'",
                      color: ok ? 'var(--heading)' : 'var(--muted)',
                    }}
                  >
                    {r.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {errors?.general ? (
          <Note tone="danger" style={{ marginTop: 16 }}>{errors.general}</Note>
        ) : null}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <Button type="submit" icon="key" disabled={!puedeEnviar} loading={loading}>
            Cambiar contraseña
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default PasswordChangeSection;
